import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export const AdminSocial = () => {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState({ platform: "youtube", url: "", caption: "", publish_date: "" });
  const [displaySettings, setDisplaySettings] = useState({ maxPosts: 6, layout: "grid" });

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/admin/social`); setPosts(data); }
    catch (err) { if (err?.response?.status !== 404) console.error("Load social:", err); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const getEmbed = (platform, url) => {
    if (platform === "youtube") {
      const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
      return m ? `https://www.youtube.com/embed/${m[1]}` : null;
    }
    return null;
  };

  const addPost = async () => {
    if (!form.url) return toast.error("URL is required");
    try {
      const embed = getEmbed(form.platform, form.url);
      await axios.post(`${API}/api/admin/social`, { ...form, embed_code: embed || "" });
      setForm({ platform: "youtube", url: "", caption: "", publish_date: "" });
      load(); toast.success("Content added!");
    } catch { toast.error("Failed to add"); }
  };

  const deletePost = async (id) => {
    if (!window.confirm("Remove this content?")) return;
    try { await axios.delete(`${API}/api/admin/social/${id}`); load(); toast.success("Removed"); } catch { toast.error("Failed"); }
  };

  const platformIcons = { youtube: "fa-youtube", instagram: "fa-instagram", tiktok: "fa-tiktok", twitter: "fa-x-twitter" };
  const platformColors = { youtube: "#FF0000", instagram: "#E1306C", tiktok: "#000", twitter: "#1DA1F2" };

  return (
    <div>
      {/* Facebook Connection Card */}
      <div style={{ background: "#fff", borderRadius: 12, padding: "1.25rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)", marginBottom: "1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: "#1877F2", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "1.2rem" }}>
            <i className="fab fa-facebook-f" />
          </div>
          <div>
            <p style={{ fontSize: ".9rem", fontWeight: 600, color: "#1F2937", margin: "0 0 .15rem" }}>Facebook Connected</p>
            <p style={{ fontSize: ".78rem", color: "#9CA3AF", margin: 0 }}>facebook.com/financialfreedomdelivered</p>
          </div>
        </div>
        <span style={{ padding: ".25rem .6rem", borderRadius: 50, fontSize: ".7rem", fontWeight: 600, background: "rgba(16,185,129,.1)", color: "#059669" }}>Active</span>
      </div>

      {/* Add Content */}
      <div style={{ background: "#fff", borderRadius: 12, padding: "1.5rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)", marginBottom: "1.5rem" }}>
        <h3 style={{ fontSize: ".95rem", fontWeight: 700, color: "#1F2937", margin: "0 0 1rem" }}>Add Social Content</h3>
        <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: ".75rem", marginBottom: ".75rem" }}>
          <select value={form.platform} onChange={e => setForm(p => ({ ...p, platform: e.target.value }))} data-testid="social-platform-select"
            style={{ padding: ".5rem", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: ".85rem" }}>
            <option value="youtube">YouTube</option>
            <option value="instagram">Instagram</option>
            <option value="tiktok">TikTok</option>
            <option value="twitter">Twitter / X</option>
          </select>
          <input value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))} placeholder="Paste content URL..." data-testid="social-url-input"
            style={{ padding: ".5rem .75rem", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: ".85rem" }} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 160px", gap: ".75rem", marginBottom: ".75rem" }}>
          <input value={form.caption} onChange={e => setForm(p => ({ ...p, caption: e.target.value }))} placeholder="Caption (optional)" data-testid="social-caption-input"
            style={{ padding: ".5rem .75rem", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: ".85rem" }} />
          <input type="date" value={form.publish_date} onChange={e => setForm(p => ({ ...p, publish_date: e.target.value }))} data-testid="social-date-input"
            style={{ padding: ".5rem .75rem", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: ".85rem" }} />
        </div>
        {form.url && form.platform === "youtube" && getEmbed("youtube", form.url) && (
          <iframe src={getEmbed("youtube", form.url)} style={{ width: "100%", height: 180, border: "none", borderRadius: 8, marginBottom: ".75rem" }} title="Preview" allowFullScreen />
        )}
        <button onClick={addPost} data-testid="social-add-button" style={{ padding: ".5rem 1.25rem", borderRadius: 8, border: "none", background: "linear-gradient(135deg, #EC4899, #F97316)", color: "#fff", cursor: "pointer", fontSize: ".82rem", fontWeight: 600 }}>
          <i className="fas fa-plus" /> Add Content
        </button>
      </div>

      {/* Display Settings */}
      <div style={{ background: "#fff", borderRadius: 12, padding: "1.25rem", boxShadow: "0 1px 3px rgba(0,0,0,.06)", marginBottom: "1.5rem" }}>
        <h4 style={{ fontSize: ".85rem", fontWeight: 700, color: "#374151", margin: "0 0 .8rem" }}>Display Settings</h4>
        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <div>
            <label style={{ fontSize: ".78rem", color: "#6B7280", display: "block", marginBottom: ".25rem" }}>Posts to show</label>
            <select value={displaySettings.maxPosts} onChange={e => setDisplaySettings(p => ({ ...p, maxPosts: parseInt(e.target.value) }))}
              style={{ padding: ".4rem .6rem", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: ".82rem" }}>
              {[3, 6, 9, 12].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: ".78rem", color: "#6B7280", display: "block", marginBottom: ".25rem" }}>Layout</label>
            <select value={displaySettings.layout} onChange={e => setDisplaySettings(p => ({ ...p, layout: e.target.value }))}
              style={{ padding: ".4rem .6rem", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: ".82rem" }}>
              <option value="grid">Grid</option>
              <option value="carousel">Carousel</option>
              <option value="masonry">Masonry</option>
            </select>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <h3 style={{ fontSize: ".95rem", fontWeight: 700, color: "#1F2937", margin: "0 0 1rem" }}>
        Content ({posts.length})
      </h3>
      {posts.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 12, padding: "3rem", textAlign: "center", color: "#9CA3AF", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
          <i className="fas fa-share-alt" style={{ fontSize: "2rem", marginBottom: ".5rem", display: "block" }} />
          <p>No social content yet. Add your first post above.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1rem" }}>
          {posts.map(p => (
            <div key={p.id} style={{ background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,.06)" }}>
              {p.embed_code ? (
                <iframe src={p.embed_code} style={{ width: "100%", height: 160, border: "none" }} title={p.caption} allowFullScreen />
              ) : (
                <div style={{ height: 100, background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className={`fab ${platformIcons[p.platform] || "fa-link"}`} style={{ fontSize: "2rem", color: platformColors[p.platform] || "#9CA3AF" }} />
                </div>
              )}
              <div style={{ padding: "1rem" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: ".4rem" }}>
                  <span style={{ fontSize: ".78rem", fontWeight: 600, textTransform: "capitalize", color: platformColors[p.platform] || "#6B7280" }}>
                    <i className={`fab ${platformIcons[p.platform] || "fa-link"}`} /> {p.platform}
                  </span>
                  <button onClick={() => deletePost(p.id)} style={{ background: "none", border: "none", color: "#EF4444", cursor: "pointer", fontSize: ".75rem" }}>
                    <i className="fas fa-trash" />
                  </button>
                </div>
                {p.caption && <p style={{ fontSize: ".82rem", color: "#6B7280", margin: 0 }}>{p.caption}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
